export type SoundDefinition = {
  id: string;
  name: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  volume: number;
  cooldownSeconds: number;
  cooldownRemainingSeconds: number;
  enabled: boolean;
  viewerEnabled: boolean;
  viewerName: string;
  viewerSortOrder: number;
  viewerVolume: number;
  viewerCooldownSeconds: number;
  viewerCooldownRemainingSeconds: number;
  lastTriggeredAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type SoundQueueItem = {
  id: string;
  soundId: string;
  soundName: string;
  status: 'queued' | 'dispatching' | 'completed' | 'failed' | 'cancelled';
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
};

export type ViewerSoundboardSettings = {
  extensionEnabled: boolean;
  publicFallbackEnabled: boolean;
  globalCooldownSeconds: number;
  viewerCooldownSeconds: number;
  maxQueueLength: number;
  configuredChannelId: string | null;
  statusMessage: string;
};

export type SoundboardSnapshot = {
  sounds: SoundDefinition[];
  queue: SoundQueueItem[];
  active: SoundQueueItem | null;
  viewer: ViewerSoundboardSettings;
  production: {
    agentId: string | null;
    agentName: string | null;
    agentConnected: boolean;
    overlayStatus: 'online' | 'starting' | 'offline' | 'disabled';
    overlayClients: number;
    localUrl: string | null;
    obsProvisioned: boolean;
  };
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body === undefined || init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const loadSoundboard = () => request<SoundboardSnapshot>('/api/v1/soundboard');

export function updateSound(soundId: string, changes: Partial<Pick<SoundDefinition, 'name' | 'volume' | 'cooldownSeconds' | 'enabled' | 'viewerEnabled' | 'viewerName' | 'viewerSortOrder' | 'viewerVolume' | 'viewerCooldownSeconds'>>): Promise<SoundDefinition> {
  return request(`/api/v1/soundboard/sounds/${encodeURIComponent(soundId)}`, { method: 'PATCH', body: JSON.stringify(changes) });
}

export function playSound(soundId: string): Promise<unknown> {
  return request(`/api/v1/soundboard/sounds/${encodeURIComponent(soundId)}/play`, { method: 'POST', body: '{}' });
}

export function stopAllSounds(): Promise<{ stopped: boolean; agentStopped: boolean; warning?: string | null }> {
  return request('/api/v1/soundboard/stop', { method: 'POST', body: '{}' });
}

export function refreshSoundOverlay(): Promise<{ ok: true; refreshed: true }> {
  return request('/api/v1/soundboard/overlay/refresh', { method: 'POST', body: '{}' });
}

export function deleteSound(soundId: string): Promise<{ deleted: true; soundId: string }> {
  return request(`/api/v1/soundboard/sounds/${encodeURIComponent(soundId)}`, { method: 'DELETE' });
}

export function updateViewerSoundboardSettings(changes: Partial<ViewerSoundboardSettings>): Promise<ViewerSoundboardSettings> {
  return request('/api/v1/soundboard/viewer-settings', { method: 'PATCH', body: JSON.stringify(changes) });
}

export async function uploadSound(file: File): Promise<SoundDefinition> {
  const form = new FormData();
  form.append('file', file, file.name);
  return request('/api/v1/soundboard/sounds', { method: 'POST', body: form });
}

export async function replaceSoundFile(soundId: string, file: File): Promise<SoundDefinition> {
  const form = new FormData();
  form.append('file', file, file.name);
  return request(`/api/v1/soundboard/sounds/${encodeURIComponent(soundId)}/file`, { method: 'POST', body: form });
}
